import { actions as usersActions } from '@/api/users';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { getUrlSearchParam } from '@payaca/helpers/urlHelper';

import Modal from '@payaca/components/modal/Modal';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { PermissionGuard } from '../../components/permissionGuard/PermissionGuard';
import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { CityPlumbingConnectionControl } from './cityPlumbingConnectionControl/CityPlumbingConnectionControl';
import { EasyPVConnectionControl } from './easyPVConnectionControl/EasyPVConnectionControl';
import { FornaxConnectionControl } from './fornaxConnectionControl/FornaxConnectionControl';
import { HeatpunkConnectionControl } from './heatpunkConnectionControl/HeatpunkConnectionControl';
import { OpenSolarConnectionControl } from './openSolarConnectionControl/OpenSolarConnectionControl';
import QuickbooksConnectionControl from './quickbooksConnectionControl/QuickbooksConnectionControl';
import { SpruceConnectionControl } from './spruceConnectionControl/SpruceConnectionControl';
import StripeConnectionControl from './stripeConnectionControl/StripeConnectionControl';
import XeroConnectionControl from './xeroConnectionControl/XeroConnectionControl';
import ZapierConnectionControl from './zapierConnectionControl/ZapierConnectionControl';

const ConnectionsPage: FunctionComponent = (): JSX.Element => {
  const dispatch = useDispatch();

  const location = useLocation();
  const params = useParams<{ service: string }>();
  const history = useHistory();
  const [
    showStripeConnectionSuccessModal,
    setShowStripeConnectionSuccessModal,
  ] = useState(false);

  const [isCompletingXeroConnection, setIsCompletingXeroConnection] =
    useState(false);
  const [
    isCompletingQuickbooksConnection,
    setIsCompletingQuickbooksConnection,
  ] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const code = useMemo(() => {
    return getUrlSearchParam(location.search, 'code');
  }, [location]);

  const realmId = useMemo(() => {
    return getUrlSearchParam(location.search, 'realmId');
  }, [location]);

  const isStripeConfirmationSuccessRedirect = useMemo(() => {
    return (
      getUrlSearchParam(
        location.search,
        'isStripeConnectionSuccessRedirect'
      ) === 'true'
    );
  }, [location]);

  const service = useMemo(() => {
    return params.service;
  }, [params]);

  useEffect(() => {
    if (isStripeConfirmationSuccessRedirect) {
      setShowStripeConnectionSuccessModal(true);
    }
  }, [isStripeConfirmationSuccessRedirect]);

  const connectWithXero = useCallback(() => {
    setIsCompletingXeroConnection(true);
    dispatch(
      usersActions.connectWithXero(code, (error: string) => {
        if (!error) {
          dispatch(
            usersActions.getProfile(() => {
              dispatch(
                usersActions.getXeroAccounts(() => {
                  history.push('/connections');
                  setIsCompletingXeroConnection(false);
                })
              );
            })
          );
        } else {
          setErrorMessage('Unable to connect with Xero');
          setIsCompletingXeroConnection(false);
        }
      })
    );
  }, [dispatch, history, code]);

  const connectWithQuickbooks = useCallback(() => {
    setIsCompletingQuickbooksConnection(true);
    dispatch(
      usersActions.connectWithQuickbooks(
        {
          code: code,
          realmId: realmId,
        },
        (error: string) => {
          if (!error) {
            dispatch(
              usersActions.getProfile(() => {
                history.push('/connections');
                setIsCompletingQuickbooksConnection(false);
              })
            );
          } else {
            setErrorMessage('Unable to connect with Quickbooks');
            setIsCompletingQuickbooksConnection(false);
          }
        }
      )
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'realmId'
  }, [dispatch, history, code]);

  useEffect(() => {
    if (code) {
      if (service === 'xero') {
        connectWithXero();
      } else if (service === 'quickbooks') {
        connectWithQuickbooks();
      }
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'connectWithQuickbooks', 'service', 'code', and 'connectWithXero'
  }, []);

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Back', to: '/personalSettings' },
        { label: 'Connections' },
      ]}
    >
      <div className="typography:prose">
        <h1 className="px-8 py-4">Connections</h1>
      </div>
      <div className="m-4 grid gap-4 lg:grid-cols-2">
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_SUPPLIERS,
            AccountsPermissions.UPDATE_CONNECTION_SUPPLIERS,
          ]}
        >
          <CityPlumbingConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_ACCOUNTING,
            AccountsPermissions.UPDATE_CONNECTION_ACCOUNTING,
          ]}
        >
          <QuickbooksConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_PAYMENT,
            AccountsPermissions.UPDATE_CONNECTION_PAYMENT,
          ]}
        >
          <StripeConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_PAYMENT,
            AccountsPermissions.UPDATE_CONNECTION_PAYMENT,
          ]}
        >
          <FornaxConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_ACCOUNTING,
            AccountsPermissions.UPDATE_CONNECTION_ACCOUNTING,
          ]}
        >
          <XeroConnectionControl />
        </PermissionGuard>
        <PermissionGuard
          renderIfHasPermissions={[
            AccountsPermissions.ADD_CONNECTION_AUTOMATION,
            AccountsPermissions.UPDATE_CONNECTION_AUTOMATION,
          ]}
        >
          <ZapierConnectionControl />
        </PermissionGuard>
        <EasyPVConnectionControl />
        <OpenSolarConnectionControl />
        <HeatpunkConnectionControl />
        <SpruceConnectionControl />
      </div>
      <Modal
        className="stripe-connection-success-modal"
        isOpen={showStripeConnectionSuccessModal}
        size={'xs'}
        onClose={() => setShowStripeConnectionSuccessModal(false)}
      >
        <h3>
          You‘ve successfully connected your Stripe account to Payaca and can
          now accept card payments.
        </h3>
      </Modal>
    </AuthenticatedPageWrapper>
  );
};

export default ConnectionsPage;
